import { isOptimisticPortalMessageId } from "./portalOptimisticMessage";

export const PORTAL_MESSAGES_PAGE_SIZE = 50;
export const PORTAL_MESSAGES_MAX_IN_MEMORY = 500;

function messageSortKey(message) {
  const numericId = Number(message?.id);
  if (Number.isFinite(numericId) && numericId > 0) return numericId;
  if (isOptimisticPortalMessageId(message?.id)) {
    const parts = String(message.id).split("-");
    const ts = Number(parts[2]);
    return Number.isFinite(ts) ? ts : 0;
  }
  return 0;
}

export function sortPortalMessages(list) {
  return [...list].sort((a, b) => {
    const keyDiff = messageSortKey(a) - messageSortKey(b);
    if (keyDiff !== 0) return keyDiff;
    const idA = isOptimisticPortalMessageId(a?.id) ? Number.MAX_SAFE_INTEGER : Number(a?.id) || 0;
    const idB = isOptimisticPortalMessageId(b?.id) ? Number.MAX_SAFE_INTEGER : Number(b?.id) || 0;
    return idA - idB;
  });
}

function shouldDropOptimisticDuplicate(optimistic, confirmed) {
  if (!isOptimisticPortalMessageId(optimistic?.id)) return false;
  if (optimistic?.sendStatus !== "sending") return false;
  if (optimistic.sender !== confirmed.sender) return false;
  if (optimistic.type !== confirmed.type) return false;

  if (optimistic.type === "text") {
    return String(optimistic.text || "").trim() === String(confirmed.text || "").trim();
  }

  return true;
}

function removeOptimisticDuplicates(list) {
  const confirmed = list.filter((m) => !isOptimisticPortalMessageId(m?.id));
  if (!confirmed.length) return list;

  return list.filter((m) => {
    if (!isOptimisticPortalMessageId(m?.id) || m?.sendStatus !== "sending") return true;
    return !confirmed.some((c) => shouldDropOptimisticDuplicate(m, c));
  });
}

function trimPortalMessages(list, mode) {
  if (list.length <= PORTAL_MESSAGES_MAX_IN_MEMORY) return list;
  if (mode === "prepend") {
    return list.slice(0, PORTAL_MESSAGES_MAX_IN_MEMORY);
  }
  return list.slice(-PORTAL_MESSAGES_MAX_IN_MEMORY);
}

/**
 * @param {"replace"|"prepend"|"merge"} mode
 */
export function mergePortalMessages(prev, incoming, mode = "replace") {
  const batch = incoming || [];

  if (mode === "replace") {
    return trimPortalMessages(sortPortalMessages(batch), "merge");
  }

  if (mode === "prepend") {
    const ids = new Set(prev.map((m) => m.id));
    const merged = sortPortalMessages([...batch.filter((m) => !ids.has(m.id)), ...prev]);
    return trimPortalMessages(removeOptimisticDuplicates(merged), "prepend");
  }

  const map = new Map(prev.map((m) => [m.id, m]));
  for (const m of batch) {
    map.set(m.id, m);
  }
  return trimPortalMessages(
    removeOptimisticDuplicates(sortPortalMessages([...map.values()])),
    "merge"
  );
}

export function maxPortalMessageId(messages) {
  if (!messages?.length) return null;
  let max = null;
  for (const message of messages) {
    if (isOptimisticPortalMessageId(message?.id)) continue;
    const id = Number(message?.id);
    if (!Number.isFinite(id)) continue;
    if (max == null || id > max) max = id;
  }
  return max;
}
